/**
 * Typed JSON fetch wrapper. Cookies are sent on every request via `credentials: include`.
 * 422 / 401 / 5xx errors throw `ApiError` with the structured body.
 */

const BASE = (import.meta.env.VITE_API_BASE_URL ?? '') as string;

export class ApiError extends Error {
  constructor(public status: number, public code: string, message: string, public details?: unknown) {
    super(message);
  }
}

export type ApiOptions = {
  query?: Record<string, string | number | undefined>;
  body?: unknown;
  headers?: Record<string, string>;
  signal?: AbortSignal;
};

async function request<T>(method: string, path: string, opts: ApiOptions = {}): Promise<T> {
  const url = new URL(`${BASE}${path}`, location.origin);
  if (opts.query) {
    for (const [k, v] of Object.entries(opts.query)) {
      if (v !== undefined && v !== null) url.searchParams.set(k, String(v));
    }
  }
  const headers: Record<string, string> = { ...(opts.headers ?? {}) };
  if (opts.body !== undefined) headers['content-type'] = 'application/json';
  // We used to auto-attach Idempotency-Key on every mutation, but the only call
  // sites that benefit are BackgroundSync replays. Sending one for every
  // interactive submit forces a D1 read + write on the idempotency_keys table
  // for no real safety win, so we skip it. Callers that genuinely need replay
  // safety can pass the header explicitly via opts.headers.

  const res = await fetch(url.toString(), {
    method,
    credentials: 'include',
    headers,
    body: opts.body !== undefined ? JSON.stringify(opts.body) : null,
    signal: opts.signal,
  });

  const text = await res.text();
  const json = text ? JSON.parse(text) : null;

  if (!res.ok) {
    const err = (json as { error?: { code?: string; message?: string; details?: unknown } } | null)?.error;
    throw new ApiError(res.status, err?.code ?? 'unknown', err?.message ?? res.statusText, err?.details);
  }
  return json as T;
}

export const api = {
  get: <T>(path: string, opts?: ApiOptions) => request<T>('GET', path, opts),
  post: <T>(path: string, body?: unknown, opts?: ApiOptions) => request<T>('POST', path, { ...opts, body }),
  patch: <T>(path: string, body?: unknown, opts?: ApiOptions) => request<T>('PATCH', path, { ...opts, body }),
  del: <T>(path: string, opts?: ApiOptions) => request<T>('DELETE', path, opts),
};
